import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { HelpCircle, ChevronDown, Mail, Sparkles, Wallet, LogIn } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import usePageSeo from "@/hooks/usePageSeo";

const CATEGORY_ICON = {
  features: Sparkles,
  value:    Wallet,
  enroll:   LogIn,
};

export default function Faq() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/faq");
        setCategories(r.data.categories || []);
      } catch (err) {
        toast.error("Could not load FAQs");
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!filter.trim()) return categories;
    const needle = filter.trim().toLowerCase();
    return categories
      .map((c) => ({
        ...c,
        entries: c.entries.filter((e) =>
          e.question.toLowerCase().includes(needle) || e.answer.toLowerCase().includes(needle)
        ),
      }))
      .filter((c) => c.entries.length > 0);
  }, [categories, filter]);

  // Build JSON-LD FAQPage from real DB entries so AI crawlers get the fresh set.
  const jsonLd = useMemo(() => {
    const items = categories.flatMap((c) => c.entries).slice(0, 20).map((e) => ({
      "@type": "Question",
      name: e.question,
      acceptedAnswer: { "@type": "Answer", text: e.answer },
    }));
    return items.length ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items,
    } : null;
  }, [categories]);

  usePageSeo({
    title: "FAQ — Dividend Portfolio Planner",
    description: "Common questions about DPP — features, pricing, and how to sign up. Backed by our live knowledge base, updated as questions come in.",
    path: "/faq",
    ogSlug: "faq",
    jsonLdId: "faq-jsonld",
    jsonLd,
  });

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="mb-8">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary flex items-center gap-2">
          <HelpCircle className="w-3.5 h-3.5" /> Frequently Asked Questions
        </div>
        <h1 className="serif text-4xl mt-2">Questions, answered</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">
          Everything visitors ask most often — from what DPP does, to what it costs, to how to enroll. Can&apos;t find your question? Ask our{" "}
          <button type="button" onClick={() => document.querySelector('[data-testid="chatbot-launcher"]')?.click()} className="text-primary underline underline-offset-2 hover:text-primary/80">on-site assistant</button>
          {" "}or use <Link to="/how-we-rank" className="text-primary underline underline-offset-2 hover:text-primary/80">Contact us</Link> to reach a human.
        </p>
      </div>

      <div className="mb-6">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search FAQs…"
          className="w-full h-11 rounded-full border border-border bg-card px-4 text-sm focus:outline-none focus:border-primary/60"
          data-testid="faq-search"
        />
      </div>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-md bg-muted animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-muted-foreground italic text-center py-12" data-testid="faq-empty">
          No FAQs match "{filter}" — try another word, or ping the chatbot.
        </div>
      ) : (
        <div className="space-y-8" data-testid="faq-categories">
          {filtered.map((cat) => {
            const Icon = CATEGORY_ICON[cat.key] || HelpCircle;
            return (
              <div key={cat.key} data-testid={`faq-cat-${cat.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-md bg-primary/10 grid place-items-center text-primary">
                    <Icon className="w-4 h-4" />
                  </div>
                  <h2 className="serif text-2xl">{cat.title}</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-3 ml-10">{cat.blurb}</p>
                <div className="space-y-2">
                  {cat.entries.map((e) => {
                    const open = openId === e.id;
                    return (
                      <Card key={e.id} data-testid={`faq-entry-${e.id}`}>
                        <button
                          type="button"
                          onClick={() => setOpenId(open ? null : e.id)}
                          className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 hover:bg-muted/30 transition-colors"
                          aria-expanded={open}
                          data-testid={`faq-toggle-${e.id}`}
                        >
                          <span className="font-medium text-base">{e.question}</span>
                          <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                        </button>
                        {open && (
                          <CardContent className="px-5 pb-5 pt-0" data-testid={`faq-answer-${e.id}`}>
                            <div className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap border-t border-border/60 pt-3">
                              {e.answer}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-12 rounded-md border border-border bg-muted/30 p-5 text-sm text-muted-foreground text-center">
        <p className="leading-relaxed">
          Still stuck? <Link to="/how-we-rank" className="underline underline-offset-2 hover:text-primary">Contact us</Link> and we&apos;ll get right back.
        </p>
      </div>
    </div>
  );
}
